import React, { useEffect } from 'react'
import * as yup from "yup";
import { Label } from '../labels'
import { ErrorState } from '../Types';
import { RadioList } from '../controls';
import { TextInput } from '../inputs';
import { SetState, ValidationHint } from '../validation';

export enum CertifierTitles {
    Secretary = "Secretary",
    PublicOfficer = "PublicOfficer",
    Other = "Other",
}

export interface Certifier {
    certifierAuthority?: boolean;
    certifierTitle?: CertifierTitles;
    certifierOtherTitle?: string;
    certifierFirstName?: string;
    certifierLastName?: string;
    certifierEmail?: string;
}

export const getCertifierShape = (deps: string[], isRequired: (...deps: any[]) => boolean) => ({
    certifierAuthority: yup
        .boolean()
        .when([...deps], {
            is: (...args: any[]) => isRequired(...args),
            then: schema => schema.required(),
        })
        .label("Do you have authority to sign official documents on behalf of the company"),
    certifierTitle: yup
        .mixed<CertifierTitles>()
        .oneOf(Object.values(CertifierTitles))
        .when(['certifierAuthority', ...deps], {
            is: (certifierAuthority?: boolean, ...args: any[]) => certifierAuthority !== undefined && isRequired(...args),
            then: schema => schema.required(),
        })
        .label("Role"),
    certifierOtherTitle: yup
        .string()
        .when(['certifierTitle', ...deps], {
            is: (certifierTitle: CertifierTitles, ...args: any[]) => certifierTitle === CertifierTitles.Other && isRequired(...args),
            then: (schema) => schema.required()
        })
        .label("Declaration certifier other role description"),
    certifierFirstName: yup
        .string()
        .when(['certifierAuthority', ...deps], {
            is: (certifierAuthority: boolean, ...args: any[]) => certifierAuthority === false && isRequired(...args),
            then: (schema) => schema.required()
        })
        .label("First name"),
    certifierLastName: yup
        .string()
        .when(['certifierAuthority', ...deps], {
            is: (certifierAuthority: boolean, ...args: any[]) => certifierAuthority === false && isRequired(...args),
            then: (schema) => schema.required()
        })
        .label("Last name"),
    certifierEmail: yup
        .string()
        .when(['certifierAuthority', ...deps], {
            is: (certifierAuthority: boolean, ...args: any[]) => certifierAuthority === false && isRequired(...args),
            then: (schema) => schema.required()
        })
        .label("Email")
});

export interface CertifierPickerProps {
    value: Certifier
    onChange: SetState<Certifier>
    errors?: ErrorState<Certifier>
    readOnly?: boolean
    className?: string
}

export const CertifierPicker: React.FC<CertifierPickerProps> = ({ value, onChange, errors, readOnly, className }) => {
    const setCertifierAuthority = (certifierAuthority?: boolean) => {
        if(readOnly)
            return;
        if(value.certifierAuthority == certifierAuthority) {
            return;
        }
        if (certifierAuthority) {
            onChange({
                certifierTitle: undefined,
                certifierOtherTitle: undefined,
                certifierFirstName: "",
                certifierLastName: "",
                certifierEmail: "",
                certifierAuthority
            },
                ['certifierAuthority']
            );
        } 
        else {
            onChange({
                certifierTitle: undefined,
                certifierOtherTitle: undefined,
                certifierAuthority
            },
                ['certifierAuthority']
            );
        }
    }

    useEffect(() => {
        if(readOnly)
            return;
        if (value.certifierTitle !== CertifierTitles.Other) {
            onChange({ certifierOtherTitle: undefined }, ValidationHint.NoValidation);
        }
    }, [value.certifierTitle]);
   
    return <div className={className}>
        <div className="row">
            <div className="col col-12">
                <Label
                    id="authorityToCertify"
                    label="Do you have authority to sign official documents on behalf of the company (e.g., you are the secretary or public officer)?"
                    error={errors?.certifierAuthority}
                    required
                >
                    <RadioList<boolean | undefined>
                        disabled={readOnly}
                        id="authorityToCertify"
                        value={value.certifierAuthority}
                        options={[
                            {
                                label: "Yes",
                                value: true,
                            },
                            {
                                label:
                                    "No, but I have appropriate authorisation to submit the form",
                                value: false,
                            },
                        ]}
                        onChange={setCertifierAuthority}
                    />
                </Label>
            </div>
        </div>

        {value.certifierAuthority === true && (
            <div className="row mt-4">
                <div className="col col-6">
                    <Label
                        id="certifierTitle"
                        label="Please select which best describes your role"
                        error={errors?.certifierTitle}
                        required
                    >
                        <RadioList<CertifierTitles | undefined>
                            disabled={readOnly}
                            id="certifierTitle"
                            value={value.certifierTitle}
                            options={[
                                {
                                    label: "Secretary",
                                    value: CertifierTitles.Secretary,
                                },
                                {
                                    label: "Public Officer",
                                    value: CertifierTitles.PublicOfficer,
                                },
                                {
                                    label: "Other",
                                    value: CertifierTitles.Other,
                                },
                            ]}
                            onChange={certifierTitle => onChange({ certifierTitle })}
                        />
                    </Label>
                </div>
                {value.certifierTitle === CertifierTitles.Other && (
                    <div className="col col-6">
                        <Label
                            id="certifierOtherTitle"
                            label="Other role description"
                            error={errors?.certifierOtherTitle}
                            required={value.certifierTitle === CertifierTitles.Other}
                        >
                            <TextInput
                                disabled={readOnly}
                                value={value.certifierOtherTitle || ''}
                                onChange={(certifierOtherTitle) => onChange({ certifierOtherTitle })}
                                size="block"
                                maxLength={400}
                            />
                        </Label>
                    </div>
                )}
            </div>
        )}

        {value.certifierAuthority === false && (
            <>
                <div className="row mt-4">
                    <div className="col col-12 mb-2">
                        <h6>
                            Details of the person with authority to sign official
                            documents:
                        </h6>
                    </div>
                    <div className="col col-6">
                        <Label
                            id="certifierFirstName"
                            label="First name"
                            className="mt-2"
                            error={errors?.certifierFirstName}
                            required={value.certifierAuthority === false}
                        >
                            <TextInput
                                disabled={readOnly}
                                value={value.certifierFirstName || ''}
                                onChange={(certifierFirstName) =>
                                    onChange({
                                        certifierFirstName,
                                    })
                                }
                                size="block"
                                maxLength={200}
                            />
                        </Label>
                        <Label
                            id="certifierLastName"
                            label="Last name"
                            className="mt-2"
                            error={errors?.certifierLastName}
                            required={value.certifierAuthority === false}
                        >
                            <TextInput
                                disabled={readOnly}
                                value={value.certifierLastName || ''}
                                onChange={(certifierLastName) => onChange({ certifierLastName })}
                                size="block"
                                maxLength={200}
                            />
                        </Label>
                    </div>
                    <div className="col col-6">
                        <Label
                            id="certifierEmail"
                            label="Email"
                            className="mt-2"
                            error={errors?.certifierEmail}
                            required={value.certifierAuthority === false}
                        >
                            <TextInput
                                type="email"
                                disabled={readOnly}
                                value={value.certifierEmail || ''}
                                onChange={(certifierEmail) => onChange({ certifierEmail })}
                                size="block"
                                maxLength={400}
                            />
                        </Label>
                        <Label
                            id="certifierTitle"
                            label="Person's role"
                            className="mt-2"
                            error={errors?.certifierTitle}
                            required={value.certifierAuthority === false}
                        >
                            <RadioList<CertifierTitles | undefined>
                                disabled={readOnly}
                                id="rdl-declarationCertifierTitle"
                                value={value.certifierTitle}
                                options={[
                                    {
                                        label: "Secretary",
                                        value: CertifierTitles.Secretary,
                                    },
                                    {
                                        label: "Public Officer",
                                        value: CertifierTitles.PublicOfficer,
                                    },
                                    {
                                        label: "Other",
                                        value: CertifierTitles.Other,
                                    },
                                ]}
                                onChange={(certifierTitle) => {
                                    onChange({ certifierTitle });
                                }}
                            />
                        </Label>
                        {value.certifierTitle === CertifierTitles.Other && (
                            <Label
                                id="certifierOtherTitle2"
                                label="Other role description"
                                className="mt-2"
                                error={errors?.certifierOtherTitle}
                                required={value.certifierTitle === CertifierTitles.Other}
                            >
                                <TextInput
                                    disabled={readOnly}
                                    value={value.certifierOtherTitle || ''}
                                    onChange={(certifierOtherTitle) =>
                                        onChange({ certifierOtherTitle })
                                    }
                                    size="block"
                                    maxLength={400}
                                />
                            </Label>
                        )}
                    </div>
                </div>
            </>
        )}
    </div>
}